import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { Button, Input, Label } from "@devhop/ui";
import { XIcon } from "lucide-react";
import { useState } from "react";

export type AssistInstructorSelection = {
  id: string;
  name: string;
  pricePerSession: number;
};

type AssistInstructorMultiSelectProps = {
  value: AssistInstructorSelection[];
  onChange: (next: AssistInstructorSelection[]) => void;
  /** suffix for react-query key so multiple instances do not clash */
  queryKeySuffix?: string;
  hint?: string;
  label?: string;
  disabled?: boolean;
  /** แสดงช่องราคาต่อครั้ง */
  showPrice?: boolean;
};

export function AssistInstructorMultiSelect({
  value,
  onChange,
  queryKeySuffix = "default",
  hint,
  label = "ຄູຊ່ວຍສອນ (ບໍ່ບັງຄັບ)",
  disabled,
  showPrice = true,
}: AssistInstructorMultiSelectProps) {
  const [pickId, setPickId] = useState("");
  const [pickPrice, setPickPrice] = useState("");
  const selectedIds = new Set(value.map((s) => s.id));

  const handleAdd = async () => {
    if (!pickId || selectedIds.has(pickId) || disabled) return;
    const item = await hydrateLookupItem<{ id: string; name: string }>(
      `${config.apiUrl}/master/instructors/lookup`,
      pickId,
    );
    if (!item) return;
    const pricePerSession = showPrice
      ? Math.max(0, Number.parseInt(pickPrice.replace(/\D/g, ""), 10) || 0)
      : 0;
    onChange([...value, { id: item.id, name: item.name, pricePerSession }]);
    setPickId("");
    setPickPrice("");
  };

  const handleRemove = (id: string) => {
    if (disabled) return;
    onChange(value.filter((s) => s.id !== id));
  };

  const handlePriceChange = (id: string, raw: string) => {
    const pricePerSession = Math.max(
      0,
      Number.parseInt(raw.replace(/\D/g, ""), 10) || 0,
    );
    onChange(
      value.map((s) => (s.id === id ? { ...s, pricePerSession } : s)),
    );
  };

  return (
    <div className="space-y-2">
      <Label className="font-medium text-sm">{label}</Label>
      {hint ? (
        <p className="text-muted-foreground text-xs">{hint}</p>
      ) : null}
      <div
        className={`flex flex-col gap-2 sm:flex-row${
          disabled ? " pointer-events-none opacity-50" : ""
        }`}
      >
        <div className="min-w-0 flex-1">
          <InfiniteCombobox<{ id: string; name: string }>
            queryKey={[
              "master",
              "instructors",
              "lookup",
              "assist-multi",
              queryKeySuffix,
            ]}
            queryFn={async (params: { search: string; pageParam: number }) => {
              const limit = 20;
              const skip = (params.pageParam - 1) * limit;
              const url = new URL(`${config.apiUrl}/master/instructors/lookup`);
              url.searchParams.set("limit", String(limit));
              url.searchParams.set("skip", String(skip));
              if (params.search) url.searchParams.set("q", params.search);
              return fetchLookupForInfinite<{ id: string; name: string }>(
                url.toString(),
                params,
              );
            }}
            preloadQueryFn={(id) =>
              hydrateLookupItem<{ id: string; name: string }>(
                `${config.apiUrl}/master/instructors/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={pickId}
            onValueChange={setPickId}
            placeholder="ຄົ້ນຫາຄູສອນ..."
            className="w-full"
            clearable
          />
        </div>
        {showPrice ? (
          <Input
            type="text"
            inputMode="numeric"
            placeholder="ລາຄາ/ຄັ້ງ (ກີບ)"
            value={pickPrice}
            disabled={disabled || !pickId}
            className="w-full sm:w-40"
            onChange={(e) => setPickPrice(e.target.value)}
          />
        ) : null}
        <Button
          type="button"
          variant="outline"
          disabled={disabled || !pickId || selectedIds.has(pickId)}
          onClick={() => void handleAdd()}
        >
          ເພີ່ມ
        </Button>
      </div>

      {value.length > 0 ? (
        <ul className="space-y-1">
          {value.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-sm"
            >
              <span className="min-w-0 truncate font-medium">{s.name}</span>
              <div className="flex shrink-0 items-center gap-2">
                {showPrice ? (
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={s.pricePerSession > 0 ? String(s.pricePerSession) : ""}
                    placeholder="ລາຄາ/ຄັ້ງ"
                    disabled={disabled}
                    className="h-8 w-28 text-right text-xs"
                    onChange={(e) => handlePriceChange(s.id, e.target.value)}
                  />
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  onClick={() => handleRemove(s.id)}
                  aria-label={`remove-${s.name}`}
                >
                  <XIcon className="size-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
